import React, { memo, type RefCallback, useState, useMemo, useCallback } from 'react';
import Popup, { type TriggerProps } from '@atlaskit/popup';
import type { ProfileProps } from '@atlaskit/atlassian-navigation';
import Spinner from '@atlaskit/spinner';
import { Box, xcss } from '@atlaskit/primitives';
import { SpotlightTarget } from '@atlaskit/onboarding';
import { AccountMenuTrigger as UnmemoizedAccountMenuTrigger } from '@atlassian/account-menu';
import { JiraEntryPointContainer } from '@atlassian/jira-entry-point-container/src/index.tsx';
import { useEntryPoint } from '@atlassian/jira-entry-point/src/controllers/use-entry-point/index.tsx';
import { useEntryPointButtonTrigger } from '@atlassian/jira-entry-point-button-trigger/src/index.tsx';
import { Nav4ProfileMenuEntryPoint } from '@atlassian/jira-atlassian-navigation-nav4-profile-content/entrypoint.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { memoWithCondition } from '@atlassian/jira-feature-flagging-utils/src/utils/memo-with-condition/index.tsx';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';

// The Nav4AccountMenu component re-composes the platform AccoutMenu component in order to leverage the performance
// benefits of EntryPoints. This was done at the time because the platform did not support EntryPoints. In the future
// it might be possible to swap this out for the platform AccountMenu component should it be updated to support EntryPoints.
export const Nav4AccountMenu: React.ComponentType<
	Pick<ProfileProps, 'onClick' | 'testId'> & {
		picture: string | undefined;
		email: string | undefined;
	}
> = memo(({ testId, picture, email }) => {
	const [isOpen, setIsOpen] = useState(false);

	const atlassianAccountId = useAccountId();
	const entryPointParams = useMemo(
		() => ({
			atlassianAccountId: atlassianAccountId ?? '',
			jiraOnboardingHubGateEnabled: fg('jira-onboarding-hub-gate') ?? false,
		}),
		[atlassianAccountId],
	);
	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		Nav4ProfileMenuEntryPoint,
		entryPointParams,
	);
	const triggerRef = useEntryPointButtonTrigger(entryPointActions, !isOpen);

	const runtimeProps = useMemo(
		() => ({
			onClosePopup: () => {
				setIsOpen(false);
			},
			testId,
			accountId: fg('shared-user-preferences') ? atlassianAccountId : undefined,
		}),
		[testId, atlassianAccountId],
	);

	const TriggerInternal = useCallback(
		(triggerProps: TriggerProps) => {
			return (
				<Trigger
					triggerRef={triggerRef}
					setIsOpen={setIsOpen}
					testId={testId}
					picture={picture}
					email={email}
					isOpen={isOpen}
					triggerProps={triggerProps}
				/>
			);
		},
		[triggerRef, setIsOpen, testId, picture, email, isOpen],
	);

	return (
		<Popup
			isOpen={isOpen}
			onClose={useCallback(() => {
				setIsOpen(false);
			}, [])}
			testId={testId}
			placement="bottom"
			trigger={TriggerInternal}
			content={() => (
				<JiraEntryPointContainer
					entryPointReferenceSubject={entryPointReferenceSubject}
					id="atlassian-navigation-profile-menu"
					packageName="atlassian-navigation-profile-menu"
					errorFallback="flag"
					fallback={
						<Box xcss={Nav4AccountMenuContentSkeletonStyles} padding="space.100">
							<Spinner />
						</Box>
					}
					runtimeProps={runtimeProps}
				/>
			)}
		/>
	);
});

const AccountMenuTrigger = memoWithCondition(
	() => fg('jfp-magma-stability-fixes-nav-misc'),
	UnmemoizedAccountMenuTrigger,
);

function Trigger({
	triggerRef,
	setIsOpen,
	testId,
	picture,
	email,
	isOpen,
	triggerProps,
}: {
	triggerProps: TriggerProps;
	triggerRef: RefCallback<HTMLElement>;
	setIsOpen: (func: (open: boolean) => boolean) => void;
	testId: string | undefined;
	picture: string | undefined;
	email: string | undefined;
	isOpen: boolean;
}) {
	const ref = useCallback(
		(element: HTMLElement | null) => {
			triggerRef(element);
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			(triggerProps.ref as RefCallback<HTMLElement>)(element);
		},
		[triggerRef, triggerProps.ref],
	);
	const onClick = useCallback(() => {
		setIsOpen((open) => !open);
	}, [setIsOpen]);
	return (
		<SpotlightTarget name="profile-spotlight">
			<AccountMenuTrigger
				ref={ref}
				onClick={onClick}
				testId={testId && `${testId}--trigger`}
				picture={picture}
				trustPicture
				email={email}
				aria-expanded={isOpen}
				aria-haspopup
				aria-controls={testId && `${testId}--content`}
				data-ds--level={triggerProps['data-ds--level']}
			/>
		</SpotlightTarget>
	);
}

// These styles are defined to ensure that the initial popup for the AccountMenu content is the same width when
// displaying the skeleton loader as when displaying the actual content.
const Nav4AccountMenuContentSkeletonStyles = xcss({
	width: '304px',
	height: '307px',
});
